import type {
  PatinfoPhiDto,
  PatinfoProfileDto,
  PatinfoPutRequestDto,
  PatinfoScheduleDto,
} from '../../types/api/patinfo';
import { PHI_FIELD, getPhiValue, setPhiValue } from '../../types/api/patinfo';

/** A touchpoint schedule available for a client to be enrolled in — sourced from the
 * account's active Registries, enriched with the underlying TouchPointScheduleDto's Type
 * (see ProGroupsClientsView.tsx's getTouchpointSchedules). */
export interface ClientTouchpointSchedule {
  registryId: string;
  name: string;
  /** TouchPointScheduleDto.Type — '1' = Monitor, '2' = Age-based (see TouchpointsView.tsx). */
  type: string;
}

export interface ClientProfileEditForm {
  fname: string;
  lname: string;
  patid: string;
  email: string;
  phone: string;
  /** Selected "Send Assessment By" radio option key (notifytype), or 'Local'. */
  howcontact: string;
  /** Copy/Paste Link send option enabled, as '1' / '0'. */
  copypaste: string;
  /** Selected Local office SRA member id for the "Local" send option. */
  localsra: string;
  extraPhi: Record<string, string>;
}

const getFirstNonEmptyString = (obj: any, keys: string[]): string => {
  for (const key of keys) {
    const value = obj?.[key];
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }
  return '';
};

const resolveFallbackNames = (profile: PatinfoProfileDto): { fname: string; lname: string } => {
  const p = profile as any;

  const fname = getFirstNonEmptyString(p, ['fname', 'firstName', 'firstname', 'first_name']);
  const lname = getFirstNonEmptyString(p, ['lname', 'lastName', 'lastname', 'last_name']);
  if (fname || lname) {
    return { fname, lname };
  }

  const displayName = getPhiValue(profile.phi ?? [], 'dname')
    || getFirstNonEmptyString(p, ['dname', 'displayName', 'name']);

  if (displayName) {
    const parts = displayName.split(/\s+/).filter(Boolean);
    return {
      fname: parts[0] ?? '',
      lname: parts.slice(1).join(' '),
    };
  }

  return { fname: '', lname: '' };
};

export function profileToClientEditForm(
  profile: PatinfoProfileDto,
  extraPhiFieldNames: string[] = []
): ClientProfileEditForm {
  const phi = profile.phi ?? [];
  const fallbackNames = resolveFallbackNames(profile);
  const extraPhi: Record<string, string> = {};
  extraPhiFieldNames.forEach((name) => {
    extraPhi[name] = getPhiValue(phi, name);
  });

  return {
    fname: getPhiValue(phi, PHI_FIELD.FNAME) || fallbackNames.fname,
    lname: getPhiValue(phi, PHI_FIELD.LNAME) || fallbackNames.lname,
    patid: getPhiValue(phi, PHI_FIELD.PATID) || profile.patientid,
    email: getPhiValue(phi, PHI_FIELD.EMAIL),
    phone: getPhiValue(phi, PHI_FIELD.PHONE),
    howcontact: getPhiValue(phi, PHI_FIELD.HOWCONTACT),
    copypaste: getPhiValue(phi, PHI_FIELD.COPYPASTE),
    localsra: getPhiValue(phi, PHI_FIELD.LOCALSRA),
    extraPhi,
  };
}

export function buildChangedClientPhi(
  original: PatinfoProfileDto,
  form: ClientProfileEditForm
): PatinfoPhiDto[] | undefined {
  const origPhi = original.phi ?? [];
  let phi = origPhi;
  let changed = false;

  const update = (field: string, value: string) => {
    if (getPhiValue(origPhi, field) !== value) {
      phi = setPhiValue(phi, field, value);
      changed = true;
    }
  };

  update(PHI_FIELD.FNAME, form.fname);
  update(PHI_FIELD.LNAME, form.lname);
  update(PHI_FIELD.PATID, form.patid);
  update(PHI_FIELD.EMAIL, form.email);
  update(PHI_FIELD.PHONE, form.phone);
  update(PHI_FIELD.HOWCONTACT, form.howcontact);
  update(PHI_FIELD.COPYPASTE, form.copypaste);
  update(PHI_FIELD.LOCALSRA, form.localsra);

  Object.entries(form.extraPhi ?? {}).forEach(([name, value]) => {
    update(name, value);
  });

  return changed ? phi : undefined;
}

/**
 * Full current-form snapshot of a profile's PHI — unlike buildChangedClientPhi (which only
 * returns a value when something actually differs, for the "is there anything to save" check),
 * this always applies every form field on top of the original PHI. Send Now/Schedule build
 * their outgoing LaunchSecureDto.Phi from this: `profile` is what was last loaded/saved, but
 * the user may have typed a new email/phone (or any other field) into the form and gone
 * straight to Send Now without hitting Update first — the launch should still go out with
 * fname/lname/patid/email/phone/etc. reflecting what's actually on screen.
 */
export function buildLaunchPhi(
  original: PatinfoProfileDto,
  form: ClientProfileEditForm
): PatinfoPhiDto[] {
  let phi = original.phi ?? [];

  const set = (field: string, value: string) => {
    phi = setPhiValue(phi, field, value);
  };

  set(PHI_FIELD.FNAME, form.fname);
  set(PHI_FIELD.LNAME, form.lname);
  set(PHI_FIELD.PATID, form.patid);
  set(PHI_FIELD.EMAIL, form.email);
  set(PHI_FIELD.PHONE, form.phone);
  set(PHI_FIELD.HOWCONTACT, form.howcontact);
  set(PHI_FIELD.COPYPASTE, form.copypaste);
  set(PHI_FIELD.LOCALSRA, form.localsra);

  Object.entries(form.extraPhi ?? {}).forEach(([name, value]) => {
    set(name, value);
  });

  return phi;
}

// String()-coerces before trim() — regid/RegistryId both nominally come through typed as
// string, but a couple of these values arrive as raw, loosely-typed backend JSON with no
// normalization layer in front of them (see ProviderProfileService.searchProfiles), so a
// numeric wire value here would otherwise throw ((4).trim is not a function) rather than
// just fail to match.
const normalizeScheduleId = (value: string | number | null | undefined): string =>
  (value === null || value === undefined ? '' : String(value)).trim().toLowerCase();

/** PatinfoScheduleDto.status values. */
const SCHEDULE_STATUS_ENABLED = '1';
const SCHEDULE_STATUS_DISABLED = '0';

/** Which of the account's touchpoint schedules this profile is currently enrolled in
 * (PatinfoProfileDto.schedule), keyed by normalized regid — seeds the Schedule section's
 * touchpoint schedule checkboxes. A present entry counts as checked unless it's explicitly
 * status='0' — older data saved before `status` existed has no way to say "disabled" other
 * than not being in the list at all, so a blank/missing status still means enabled. */
export function buildInitialSelectedScheduleIds(profile: PatinfoProfileDto): Record<string, boolean> {
  const map: Record<string, boolean> = {};
  (profile.schedule ?? []).forEach((item) => {
    const id = normalizeScheduleId(item.regid);
    if (id) {
      map[id] = item.status !== SCHEDULE_STATUS_DISABLED;
    }
  });
  return map;
}

/** Each enrolled schedule's current startdate, keyed by normalized regid — seeds the
 * Schedule section's per-schedule "Start Date" input for Monitor-type schedules. */
export function buildInitialScheduleStartDates(profile: PatinfoProfileDto): Record<string, string> {
  const map: Record<string, string> = {};
  (profile.schedule ?? []).forEach((item) => {
    const id = normalizeScheduleId(item.regid);
    if (id) {
      map[id] = item.startdate || '';
    }
  });
  return map;
}

const scheduleSignature = (schedule: PatinfoScheduleDto[]): string =>
  schedule
    .map((item) => [
      normalizeScheduleId(item.regid),
      item.schedtype,
      item.startdate,
      item.schednext,
      item.terminate,
      item.episode,
      item.status,
    ].join(':'))
    .sort()
    .join(',');

/** Builds the full desired PatinfoScheduleDto[] — one explicit entry per account touchpoint
 * schedule (full-replace, same convention as phi), status='1'/'0' reflecting whether it's
 * currently checked. Unchecking a schedule has no other visible trace once it's removed from
 * an array (an absent regid doesn't read as a change to anything), so every schedule the UI
 * shows a checkbox for gets sent with an explicit status rather than only the checked ones —
 * that's what actually makes "I unchecked this" a detectable, persistable action. Preserves
 * schednext/terminate/episode from any existing entry for that regid (server/schedule-engine-
 * owned fields this UI doesn't edit). */
function buildScheduleFromSelection(
  original: PatinfoProfileDto,
  touchpointSchedules: ClientTouchpointSchedule[],
  selectedScheduleIds: Record<string, boolean>,
  scheduleStartDates: Record<string, string>
): PatinfoScheduleDto[] {
  const existingByRegId = new Map<string, PatinfoScheduleDto>();
  (original.schedule ?? []).forEach((item) => {
    const id = normalizeScheduleId(item.regid);
    if (id) {
      existingByRegId.set(id, item);
    }
  });

  return touchpointSchedules.map((schedule) => {
    const id = normalizeScheduleId(schedule.registryId);
    const existing = existingByRegId.get(id);
    const checked = !!selectedScheduleIds[id];
    return {
      regid: schedule.registryId,
      schedtype: schedule.type,
      startdate: scheduleStartDates[id] ?? existing?.startdate ?? '',
      schednext: existing?.schednext ?? '',
      terminate: existing?.terminate ?? '',
      episode: existing?.episode ?? '',
      status: checked ? SCHEDULE_STATUS_ENABLED : SCHEDULE_STATUS_DISABLED,
    };
  });
}

export function buildClientProfilePutPayload(
  original: PatinfoProfileDto,
  form: ClientProfileEditForm,
  scheduleSelection?: {
    touchpointSchedules: ClientTouchpointSchedule[];
    selectedScheduleIds: Record<string, boolean>;
    scheduleStartDates: Record<string, string>;
  }
): PatinfoPutRequestDto | null {
  const changedPhi = buildChangedClientPhi(original, form);
  // patid now lives in PHI, but the DTO also carries a top-level patientid used
  // elsewhere (search results, pagination) — keep it in sync whenever the PHI
  // patid actually differs from what the profile started with.
  const patientidChanged = form.patid !== original.patientid ? form.patid : undefined;

  let nextSchedule: PatinfoScheduleDto[] | undefined;
  if (scheduleSelection) {
    const candidate = buildScheduleFromSelection(
      original,
      scheduleSelection.touchpointSchedules,
      scheduleSelection.selectedScheduleIds,
      scheduleSelection.scheduleStartDates
    );
    if (scheduleSignature(original.schedule ?? []) !== scheduleSignature(candidate)) {
      nextSchedule = candidate;
    }
  }

  if (!changedPhi && !patientidChanged && !nextSchedule) {
    return null;
  }

  return {
    patcode: original.patcode,
    ...(patientidChanged ? { patientid: patientidChanged } : {}),
    ...(changedPhi ? { phi: changedPhi } : {}),
    ...(nextSchedule ? { schedule: nextSchedule } : {}),
  };
}

export function getClientDisplayName(profile: PatinfoProfileDto): string {
  return [getPhiValue(profile.phi, PHI_FIELD.FNAME), getPhiValue(profile.phi, PHI_FIELD.LNAME)]
    .filter(Boolean)
    .join(' ') || profile.patientid || profile.patcode;
}
